import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Grid,
  Divider,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from '@mui/material';
import { FaArrowLeft, FaFileWord, FaSave, FaLink, FaUnlink, FaUser, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  BackButton, 
  Title, 
  Content, 
  MainSection,
  HeaderButton
} from '../../common/styles';
import { COLORS } from '../../../constants/radar';
import { candidateLabel, cvDownloadFilename, formatCvCode } from '../../../utils/cvCode';
import { useAuth } from '../../../contexts/AuthContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAbility } from '../../../contexts/AbilityContext';
import { ACTIONS, SUBJECTS } from '../../../constants/permissions';
import { CVProvider, useCV } from './contexts/CVContext';
import PersonalDetails from './components/PersonalDetails';
import OnlineProfiles from './components/OnlineProfiles';
import ProfessionalSummary from './components/ProfessionalSummary';
import Languages from './components/Languages';
import Courses from './components/Courses';
import Education from './components/Education';
import Certifications from './components/Certifications';
import EmploymentHistory from './components/EmploymentHistory';
import Skills from './components/Skills';
import CVPreview from './components/CVPreview';
import CVCompletionGauge from './components/CVCompletionGauge';
import CVErrorState from './components/CVErrorState';
import { generateDocxFile } from './helpers/ProcessTemplate';
import { saveAs } from 'file-saver';
import { cvApi } from '../../../services/api';
import useCVStore from '../../../store/cvStore';

const CVContent = ({ onBack, disableEmployeeChange, readonly }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const { user } = useAuth();
  const ability = useAbility();
  const { cvData, cvDataWithMetadata, saveCV, deleteCV, isDirty, getError, isLoading } = useCV();
  const { showAlert } = useAlert();
  
  // Initialize the CV store to set up notifications
  useCVStore();
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [templateLoaded, setTemplateLoaded] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showObfuscationDialog, setShowObfuscationDialog] = useState(false);
  const [obfuscationOptions, setObfuscationOptions] = useState({
    fullName: true,
    employmentHistory: true,
    employerObfuscationType: 'previous'
  });

  // Function to load the template
  const loadTemplate = useCallback(async () => {
    try {
      // Fetch and cache the template
      await cvApi.getCvTemplate();
      setTemplateLoaded(true);
    } catch (error) {
      console.error('Error loading CV template');
      setTemplateLoaded(false);
    }
  }, []);

  // Preload CV template when component mounts
  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      navigateBack();
    }
  };

  const navigateBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedChangesDialog(false);
  };

  const handleConfirmNavigation = () => {
    setShowUnsavedChangesDialog(false);
    navigateBack();
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrev = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleDownloadWord = async () => {
    try {
      setShowObfuscationDialog(true);
    } catch (error) {
      console.error('Error generating document');
      showAlert(`Error generating DOCX file: ${error.message}`, 'error');
    }
  };

  const handleObfuscationChange = (option) => {
    setObfuscationOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const handleEmployerObfuscationTypeChange = (event) => {
    setObfuscationOptions(prev => ({
      ...prev,
      employerObfuscationType: event.target.value
    }));
  };

  const handleGenerateDocx = async () => {
    try {
      setIsGeneratingDoc(true);
      setShowObfuscationDialog(false);
      const blob = await generateDocxFile(cvDataWithMetadata, obfuscationOptions);
      // ADR-109: anonymous filename when the name is obfuscated
      saveAs(blob, cvDownloadFilename(cvData, obfuscationOptions.fullName));
    } catch (error) {
      console.error('Error generating document:', error);
      showAlert(`Error generating DOCX file: ${error.message}`, 'error');
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  const handleSaveCV = async () => {
    try {
      setIsSaving(true);
      const success = await saveCV();
      if (success) {
        showAlert('CV saved successfully!', 'success');
      } else {
        showAlert(getError() || 'Error saving CV data', 'error');
      }

    } catch (error) {
      console.error('Error saving CV');
      showAlert(`Error saving CV: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCV = () => {
    setShowDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      setShowDeleteConfirmDialog(false);
      const success = await deleteCV();
      if (success) {
        showAlert('CV deleted successfully!', 'success');
      } else {
        showAlert(getError() || 'Error deleting CV', 'error');
      }
    } catch (error) {
      console.error('Error deleting CV');
      showAlert(`Error deleting CV: ${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmDialog(false);
  };

  // Template button states
  const getTemplateButtonContent = () => {
    if (isGeneratingDoc) return <><FaFileWord /> Generating...</>;
    if (!templateLoaded) return <><FaFileWord /> Loading template...</>;
    return <><FaFileWord /> Word</>;
  };

  // Function to determine if the save button should be disabled
  const isSaveButtonDisabled = isSaving || !isDirty;

  // Function to determine if the button should be disabled
  const isDownloadButtonDisabled = isGeneratingDoc || !templateLoaded || isDirty;

  const steps = [
    {
      label: 'Personal Details',
      description: 'Add your name, contact information, and other personal details.'
    },
    {
      label: 'Online Profiles',
      description: 'Add links to your professional online presence (LinkedIn, GitHub, portfolio, etc.).'
    },
    {
      label: 'Professional Summary',
      description: `Write 2-4 short, energetic sentences about how great you are.
Mention the role and what you did.
What were the big achievements?
Describe your motivation and list your skills.`,
    },
    {
      label: 'Skills',
      description: 'List your technical and soft skills relevant to your profession.'
    },
    {
      label: 'Employment History',
      description: 'Add your work experience including job titles, companies, dates, and responsibilities.'
    },
    {
      label: 'Education',
      description: 'Include your educational background such as degrees, institutions, and graduation dates.'
    },
    {
      label: 'Courses',
      description: 'List any relevant courses you have completed.'
    },
    {
      label: 'Certifications',
      description: 'Add any professional certifications you have earned.'
    },
    {
      label: 'Languages',
      description: 'Specify your language proficiency levels.'
    }
  ];

  // Render the appropriate content based on the active step
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <PersonalDetails />;
      case 1:
        return <OnlineProfiles />;
      case 2:
        return <ProfessionalSummary />;
      case 3:
        return <Skills />;
      case 4:
        return <EmploymentHistory />;
      case 5:
        return <Education />;
      case 6:
        return <Courses />;
      case 7:
        return <Certifications />;
      case 8:
        return <Languages />;
      default:
        return (
          <Box sx={{ 
            p: 3, 
            bgcolor: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px dashed #dee2e6',
            mb: 4,
            minHeight: '200px'
          }}>
            <Typography sx={{ color: '#6c757d', fontStyle: 'italic' }}>
              This section is under development. You will be able to add and edit your {steps[activeStep].label.toLowerCase()} here soon.
            </Typography>
          </Box>
        );
    }
  };

  // Show error state if there's an error from loading a specific CV ID
  /*const storeError = getError();
  if (storeError && !isLoading) {
    return (
      <CVErrorState
        errorMessage={storeError}
        onBack={handleBack}
        backButtonText={onBack ? 'Back to Repository' : 'Back to Home'}
      />
    );
  }*/

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> {onBack ? 'Back to Repository' : 'Back to Home'}
          </BackButton>
          <Title>
            {cvData?.employee?.fullName ? cvData.employee.fullName : 'New CV'}
          </Title>
          {isDirty && !readonly && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              ml: 2, 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              px: 2, 
              py: 0.5, 
              borderRadius: 1,
              border: '1px solid rgba(255, 193, 7, 0.5)'
            }}>
              <FaExclamationTriangle color="#f59e0b" style={{ marginRight: '8px' }} />
              <Typography variant="body2" color="warning.dark" fontWeight="medium">
                Unsaved changes
              </Typography>
            </Box>
          )}
        </HeaderLeft>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {cvData?.code && (
            <>
              {/* ADR-109: CV code, presented like the other header metrics */}
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                  CV Code
                </Typography>
                <Tooltip title="Printed on generated documents as the Candidate No. — clients quote it when they refer to this CV. Click to copy.">
                  <Typography
                    variant="body1"
                    onClick={() => navigator.clipboard?.writeText(cvData.code)}
                    sx={{ fontWeight: 600, letterSpacing: '0.05em', cursor: 'pointer', color: COLORS.PRIMARY }}
                  >
                    {formatCvCode(cvData.code)}
                  </Typography>
                </Tooltip>
              </Box>
            </>
          )}
          <Divider orientation="vertical" flexItem sx={{ height: 40, my: 'auto' }} />
          <CVCompletionGauge />
          <Divider orientation="vertical" flexItem sx={{ height: 40, my: 'auto' }} />
          {!readonly && (
            <Tooltip 
              title={isDirty ? "Save your changes" : "No changes to save"} 
              placement="bottom"
            >
              <span>
                <HeaderButton 
                  onClick={handleSaveCV} 
                  disabled={isSaveButtonDisabled}
                  sx={{ 
                    opacity: isSaveButtonDisabled ? 0.7 : 1,
                    ...(isDirty && { 
                      backgroundColor: COLORS.PRIMARY,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#0e5a8a'
                      }
                    })
                  }}
                >
                  {isSaving ? <><FaSave /> Saving...</> : <><FaSave /> Save</>}
                </HeaderButton>
              </span>
            </Tooltip>
          )}
          <Tooltip 
            title={isDirty ? "Save changes before exporting" : "Download your CV as a Word document"} 
            placement="bottom"
          >
            <span> {/* Wrapper needed for disabled buttons */}
              <HeaderButton 
                onClick={handleDownloadWord} 
                disabled={isDownloadButtonDisabled}
                sx={{ opacity: isDownloadButtonDisabled ? 0.7 : 1 }}
              >
                {getTemplateButtonContent()}
              </HeaderButton>
            </span>
          </Tooltip>
          {!readonly && cvData?.id && ability.can(ACTIONS.DELETE, SUBJECTS.MY_CV) && (
            <>
              <Divider orientation="vertical" flexItem sx={{ height: 40, my: 'auto' }} />
              <Tooltip 
                title="Delete this CV permanently" 
                placement="bottom"
              >
                <span>
                  <HeaderButton 
                    onClick={handleDeleteCV} 
                    disabled={isDeleting}
                    sx={{ 
                      opacity: isDeleting ? 0.7 : 1,
                      backgroundColor: '#DC2626',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#B91C1C'
                      }
                    }}
                  >
                    {isDeleting ? <><FaTrash /> Deleting...</> : <><FaTrash /> Delete</>}
                  </HeaderButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </Header>
      <Content>
        <MainSection>
          <Grid 
            container 
            spacing={3}
            sx={{
              width: '100%',
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '2fr 1fr'
              },
              gap: '24px',
              '& .MuiGrid-item': {
                padding: '0',
                width: '100%'
              }
            }}
          >
            {/* Left side - Editor */}
            <Grid item>
              <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* Stepper navigation */}
                <Box sx={{ 
                  flex: { xs: '1 1 auto', sm: '0 0 220px' },
                  mb: { xs: 3, sm: 0 }
                }}>
                  <Stepper nonLinear orientation="vertical" activeStep={activeStep}>
                    {steps.map((step, index) => (
                      <Step key={step.label} active={activeStep === index} completed={false}>
                        <StepLabel 
                          onClick={handleStep(index)} 
                          sx={{ cursor: 'pointer' }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                            {step.label}
                          </Typography>
                        </StepLabel>
                        <StepContent TransitionProps={{ unmountOnExit: false }}>
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                              {step.description}
                            </Typography>
                          </Box>
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                {/* Content editor */}
                <Box sx={{ 
                  flex: 1, 
                  bgcolor: 'white', 
                  borderRadius: '4px',
                  p: 3,
                  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: COLORS.PRIMARY,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      fontSize: '16px',
                      fontWeight: 500
                    }}>
                      {activeStep + 1}
                    </Box>
                    <Typography variant="h6" sx={{ color: COLORS.PRIMARY }}>
                      {steps[activeStep].label}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {steps[activeStep].description}
                    </Typography>
                  </Box>

                  {/* Dynamic content based on active step */}
                  {renderStepContent(activeStep)}

                  {/* Navigation buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    mt: 4,
                    pt: 3,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Box
                      onClick={() => activeStep > 0 && handlePrev()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: activeStep > 0 ? COLORS.PRIMARY : 'text.disabled',
                        cursor: activeStep > 0 ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          opacity: activeStep > 0 ? 0.8 : 1
                        }
                      }}
                    >
                      <FaArrowLeft style={{ marginRight: '8px' }} /> Previous
                    </Box>
                    <Box
                      onClick={() => activeStep < steps.length - 1 && handleNext()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: activeStep < steps.length - 1 ? COLORS.PRIMARY : 'text.disabled',
                        cursor: activeStep < steps.length - 1 ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          opacity: activeStep < steps.length - 1 ? 0.8 : 1
                        }
                      }}
                    >
                      Next <FaArrowLeft style={{ marginLeft: '8px', transform: 'rotate(180deg)' }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            {/* Right side - CV Preview */}
            <Grid item>
              <CVPreview />
            </Grid>
          </Grid>
        </MainSection>
      </Content>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog
        open={showUnsavedChangesDialog}
        onClose={handleCancelNavigation}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
      >
        <DialogTitle id="unsaved-changes-dialog-title">
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="unsaved-changes-dialog-description">
            You have unsaved changes that will be lost if you navigate away from this page. 
            Would you like to save your changes before leaving?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmNavigation} color="error">
            Discard Changes
          </Button>
          <Button onClick={handleCancelNavigation} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              await handleSaveCV();
              navigateBack();
            }} 
            variant="contained" 
            color="primary"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save & Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Obfuscation Options Dialog */}
      <Dialog
        open={showObfuscationDialog}
        onClose={() => setShowObfuscationDialog(false)}
        aria-labelledby="obfuscation-dialog-title"
        aria-describedby="obfuscation-dialog-description"
      >
        <DialogTitle id="obfuscation-dialog-title">
          Obfuscation Options
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="obfuscation-dialog-description">
            Select the information you want to obfuscate in your CV
          </DialogContentText>
          <FormGroup sx={{ mt: 2 }}>
            <FormControlLabel 
              control={
                <Checkbox 
                  checked={obfuscationOptions.fullName} 
                  onChange={() => handleObfuscationChange('fullName')} 
                />
              } 
              label={
                obfuscationOptions.fullName && cvData?.code
                  ? `Full Name — will be printed as "${candidateLabel(cvData.code)}"`
                  : 'Full Name'
              }
            />
            <FormControlLabel 
              control={
                <Checkbox 
                  checked={obfuscationOptions.employmentHistory} 
                  onChange={() => handleObfuscationChange('employmentHistory')} 
                />
              } 
              label="Employment History" 
            />
            {obfuscationOptions.employmentHistory && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    Employer Obfuscation
                  </FormLabel>
                  <RadioGroup
                    value={obfuscationOptions.employerObfuscationType}
                    onChange={handleEmployerObfuscationTypeChange}
                  >
                    <FormControlLabel 
                      value="previous" 
                      control={<Radio size="small" />} 
                      label="Previous Employers only" 
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                    <FormControlLabel 
                      value="all" 
                      control={<Radio size="small" />} 
                      label="All Employers (including current)" 
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowObfuscationDialog(false)} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleGenerateDocx} 
            variant="contained" 
            color="primary"
          >
            Generate DOCX
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete CV
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this CV? This action cannot be undone.
            <br /><br />
            <strong>CV:</strong> {cvData?.employee?.fullName || 'Untitled CV'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete CV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// Wrap the main component with the CVProvider
const MyCV = ({ cvId, employee, onBack, fromParams, isNew, disableEmployeeChange, autoLinkEmployee, readonly = false }) => {
  const { user } = useAuth();
  const params = useParams();
  
  // Determine the CV ID:
  // 1. Explicit cvId prop
  // 2. From URL params if fromParams is true
  // 3. null if isNew is true
  // 4. null as default
  let id = cvId;
  if (!id && fromParams) {
    id = params.id;
  }
  if (isNew) {
    id = null;
  }
  
  const navigate = useNavigate();
  
  // If no onBack provided, create one
  const handleBack = onBack || (() => {
    navigate('/cv-repository');
  });
  
  return (
    <CVProvider 
      employee={employee || user?.employee} 
      cvId={id}
      disableEmployeeChange={disableEmployeeChange}
      autoLinkEmployee={autoLinkEmployee}
      readonly={readonly}
    >
      <CVContent onBack={handleBack} disableEmployeeChange={disableEmployeeChange} readonly={readonly} />
    </CVProvider>
  );
};

export default MyCV; 